import { cn, focusRing } from "@simpro/ui";
import { Link } from "react-router";
import { initials } from "@/lib/initials";
import { SectionHeading } from "@/shared/components/SectionHeading";
import { Reveal } from "@/shared/motion/Reveal";
import { page } from "@/shared/styles/layout";
import { pill } from "@/shared/styles/pill";
import type { PublicStaff } from "../schemas/booking.schemas";

/**
 * The team (design note salon-home-landing.md section 5, item 9; TDD-SA FR-SA-025, FR-SA-138,
 * FR-SA-190): every stylist, each with a way to book with them.
 *
 * Drawn as the Organic Wellness board's green band (design note salon-booking-organic-wellness.md
 * section 6 item 6): it rises out of the cream on rounded shoulders, and each stylist stands in a tall
 * arch, their name in the display face, their title in pale sage, and a sage-outlined "Book with" pill
 * that opens booking with them chosen. A photo appears only when the salon publishes one; otherwise
 * the arch shows the stylist's initials. On a desktop the cards sit four across and rise in turn, the
 * photo zooming slowly under the pointer; on a phone and a tablet they form a row that scrolls sideways.
 * A column keeps a card's width however few stylists there are, so one stylist is one card, not a
 * band-wide picture (design note salon-booking-organic-wellness.md section 10.2). Under the featured
 * treatment (`joined`) the band carries straight on from it, with no shoulders of its own.
 */
export function TeamStrip({
  staff,
  heading,
  joined = false,
}: {
  staff: readonly PublicStaff[];
  heading?: string | null;
  joined?: boolean;
}) {
  if (staff.length === 0) return null;
  const first = (name: string) => name.trim().split(/\s+/)[0] ?? name;

  return (
    <section aria-labelledby="team-heading" className={cn("on-ink bg-bar text-on-bar", !joined && "band-top")}>
      <div className={cn(page, "flex flex-col gap-12 pb-20 md:pb-28 lg:pb-36", joined ? "pt-4" : "pt-20 md:pt-28 lg:pt-36")}>
        <SectionHeading id="team-heading" title={heading ?? "Hands you can trust"} onInk />
        <ul className="-mx-4 flex snap-x scroll-px-4 gap-6 overflow-x-auto px-4 pb-2 md:-mx-8 md:scroll-px-8 md:px-8 lg:mx-0 lg:grid lg:grid-cols-[repeat(auto-fill,minmax(15rem,20rem))] lg:overflow-visible lg:px-0 lg:pb-0">
          {staff.map((person, index) => (
            <Reveal
              as="li"
              key={person.id}
              index={index}
              className="group flex w-[15rem] shrink-0 snap-start flex-col gap-4 sm:w-[17rem] lg:w-auto"
            >
              <div className="aspect-[4/5] overflow-hidden rounded-[12.5rem_12.5rem_1.75rem_1.75rem] bg-bar-tint">
                {person.photoUrl ? (
                  <img
                    src={person.photoUrl}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="size-full object-cover transition-transform duration-1000 ease-[cubic-bezier(0.3,1,0.4,1)] group-hover:scale-[1.06] motion-reduce:transition-none"
                  />
                ) : (
                  <span aria-hidden="true" className="grid size-full place-items-center text-on-bar-muted">
                    <span className="display-hero text-[clamp(3.5rem,6vw,5rem)]">{initials(person.displayName)}</span>
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <h3 className="headline text-[1.875rem] leading-tight text-on-bar">{person.displayName}</h3>
                {person.title ? <p className="text-[15px] text-on-bar-muted">{person.title}</p> : null}
              </div>
              <Link
                to={`/book?stylist=${encodeURIComponent(person.id)}`}
                viewTransition
                className={cn(pill.base, pill.onInk, pill.sm, "self-start", focusRing)}
              >
                Book with {first(person.displayName)}
              </Link>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}
